import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ArrowLeft, Plus, Power } from 'lucide-react'
import { departamentulRolului, numeDepartament, type DepartamentSlug } from '@b4l/shared'
import { api } from '../../lib/api'
import { AlegeDepartamente, listaDepartamente } from '../../components/taskuri/AlegeDepartamente'
import { useAuthStore } from '../../stores/auth'
import { AntetPagina } from '../../components/ui/AntetPagina'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { StareLista } from '../../components/ui/StareLista'
import { CapSortabil, useSortare } from '../../components/ui/Sortare'
import { useConfirmare } from '../../components/ui/Confirmare'
import { aziIso, mesajEroare } from './date'

/**
 * Sabloanele recurente — D300 trimestrial, inchiderea de luna, raportul saptamanal.
 *
 * Fiecare repetare creeaza un task NOU, la „De preluat" in departamentul sablonului (nu la un om:
 * il preia cine are timp). Cel vechi ramane cu istoricul lui — altfel „cine a depus D300 in
 * trimestrul trecut si cand" n-ar mai avea raspuns. Fiecare vede si administreaza sabloanele
 * departamentului lui; adminul, pe toate.
 */

type Cadenta = 'zilnic' | 'saptamanal' | 'lunar' | 'trimestrial' | 'anual'

const CADENTE: [Cadenta, string][] = [
  ['zilnic', 'Zilnic'],
  ['saptamanal', 'Săptămânal'],
  ['lunar', 'Lunar'],
  ['trimestrial', 'Trimestrial'],
  ['anual', 'Anual'],
]
const ETICHETA = Object.fromEntries(CADENTE) as Record<Cadenta, string>
const CU_ZIUA_LUNII: Cadenta[] = ['lunar', 'trimestrial', 'anual']

interface Sablon {
  id: number
  title: string
  description: string | null
  cadence: Cadenta
  dayOfMonth: number
  leadDays: number
  interval: number
  active: boolean
  nextRun: string
  category: { id: number; slug: DepartamentSlug; name: string } | null
}

type Coloana = 'titlu' | 'departament' | 'frecventa' | 'urmatoarea'

const zi = (s: string) =>
  new Date(s).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short', year: 'numeric' })
const CHEIE = ['taskuri', 'recurente'] as const

export function RecurentePage() {
  const eu = useAuthStore((s) => s.user)
  const poateScrie = useAuthStore((s) => s.permissions.includes('tasks:write'))
  const admin = eu?.role === 'admin'
  const alMeu = eu ? departamentulRolului(eu.role) : null
  const qc = useQueryClient()
  const { confirma } = useConfirmare()
  const [nou, setNou] = useState(false)
  const sortare = useSortare<Coloana>('urmatoarea', {
    titlu: 'asc',
    departament: 'asc',
    frecventa: 'asc',
    urmatoarea: 'asc',
  })

  const q = useQuery<{ sabloane: Sablon[] }>({
    queryKey: CHEIE,
    queryFn: async () => (await api.get('/tasks/recurente')).data,
  })

  const comuta = useMutation({
    mutationFn: async (s: Sablon) =>
      (await api.patch(`/tasks/recurente/${s.id}`, { active: !s.active })).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: CHEIE }),
    onError: (e) => toast.error(mesajEroare(e, 'Nu s-a putut schimba.')),
  })

  const ordinea: Record<Cadenta, number> = {
    zilnic: 1,
    saptamanal: 2,
    lunar: 3,
    trimestrial: 4,
    anual: 5,
  }
  const sabloane = sortare.ordoneaza(q.data?.sabloane ?? [], (s, k) =>
    k === 'titlu'
      ? s.title
      : k === 'departament'
        ? numeDepartament(s.category?.slug ?? '')
        : k === 'frecventa'
          ? ordinea[s.cadence]
          : new Date(s.nextRun),
  )

  return (
    <div className="grid gap-4 p-4 pb-16 [&>*]:min-w-0">
      <AntetPagina
        titlu="Task-uri recurente"
        descriere="La fiecare scadență intră un task nou la „De preluat”, în departamentul șablonului."
        explicatie="Task-ul apare cu câteva zile înainte de termen, ca să fie timp să-l facă cineva. Un șablon oprit nu mai generează task-uri; cele deja create rămân."
        actiuni={
          <>
            <Link
              to="/task-uri"
              className="inline-flex items-center gap-1.5 rounded-caseta border border-b4l-border bg-b4l-surface px-3 py-2 text-corp text-b4l-text hover:bg-b4l-bg"
            >
              <ArrowLeft size={15} aria-hidden="true" /> Înapoi la Task-uri
            </Link>
            {poateScrie && (admin || alMeu) && (
              <Button onClick={() => setNou(true)}>
                <Plus size={15} aria-hidden="true" /> Șablon nou
              </Button>
            )}
          </>
        }
      />

      <StareLista
        interogare={q}
        gol={sabloane.length === 0}
        mesajGol="Niciun șablon încă. Bune candidate: D300 trimestrial, închiderea de lună, raportul săptămânal."
      >
        <div className="overflow-x-auto rounded-caseta border border-b4l-border bg-b4l-surface">
          <table className="w-full min-w-[40rem] text-corp">
            <thead className="bg-b4l-bg-soft text-eticheta uppercase tracking-wide text-b4l-text-dim">
              <tr>
                <CapSortabil sortare={sortare} coloana="titlu">
                  Titlu
                </CapSortabil>
                <CapSortabil sortare={sortare} coloana="departament">
                  Departament
                </CapSortabil>
                <CapSortabil sortare={sortare} coloana="frecventa">
                  Frecvență
                </CapSortabil>
                <CapSortabil sortare={sortare} coloana="urmatoarea">
                  Următoarea
                </CapSortabil>
                <th className="px-3 py-2 text-left font-semibold">Activ</th>
              </tr>
            </thead>
            <tbody>
              {sabloane.map((s) => {
                const alNostru = admin || (!!alMeu && s.category?.slug === alMeu)
                return (
                  <tr
                    key={s.id}
                    className={`border-t border-b4l-border ${s.active ? '' : 'text-b4l-text-dim'}`}
                  >
                    <td className="px-3 py-2">
                      <span className="font-medium">{s.title}</span>
                      {s.description && (
                        <span className="block text-mic text-b4l-text-muted">{s.description}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {s.category ? numeDepartament(s.category.slug) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {s.interval > 1
                        ? `${ETICHETA[s.cadence]}, la fiecare ${s.interval}`
                        : ETICHETA[s.cadence]}
                      {CU_ZIUA_LUNII.includes(s.cadence) && (
                        <span className="block text-mic text-b4l-text-muted">
                          ziua {s.dayOfMonth}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{s.active ? zi(s.nextRun) : 'oprit'}</td>
                    <td className="px-3 py-2">
                      {poateScrie && alNostru ? (
                        <button
                          type="button"
                          onClick={async () => {
                            if (
                              s.active &&
                              !(await confirma({
                                titlu: 'Oprești șablonul?',
                                mesaj: `„${s.title}” nu mai generează task-uri. Cele deja create rămân.`,
                                buton: 'Oprește',
                              }))
                            )
                              return
                            comuta.mutate(s)
                          }}
                          aria-label={s.active ? `Oprește „${s.title}”` : `Pornește „${s.title}”`}
                          className={`rounded-md p-1 hover:bg-b4l-bg ${s.active ? 'text-b4l-green' : 'text-b4l-text-dim'}`}
                        >
                          <Power size={16} />
                        </button>
                      ) : (
                        <span className="text-mic">{s.active ? 'da' : 'nu'}</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </StareLista>

      {nou && <FormularSablon alMeu={alMeu} admin={admin} laInchidere={() => setNou(false)} />}
    </div>
  )
}

const camp =
  'w-full rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-2 text-corp text-b4l-text placeholder:text-b4l-text-dim'
const eticheta = 'text-mic font-semibold text-b4l-text-muted'

function FormularSablon({
  alMeu,
  admin,
  laInchidere,
}: {
  alMeu: DepartamentSlug | null
  admin: boolean
  laInchidere: () => void
}) {
  const qc = useQueryClient()
  const [titlu, setTitlu] = useState('')
  const [alese, setAlese] = useState<DepartamentSlug[]>(alMeu ? [alMeu] : [])
  const dep = alese[0] ?? null
  const [cadenta, setCadenta] = useState<Cadenta>('lunar')
  const [ziuaLunii, setZiuaLunii] = useState(1)
  const [prima, setPrima] = useState('')
  const [descriere, setDescriere] = useState('')

  const salveaza = useMutation({
    mutationFn: async () =>
      (
        await api.post('/tasks/recurente', {
          title: titlu.trim(),
          departament: dep,
          // Adminul poate alege mai multe: cate un sablon pe departament (16.10.2026).
          departamente: alese,
          cadence: cadenta,
          dayOfMonth: ziuaLunii,
          nextRun: prima,
          description: descriere.trim() || null,
        })
      ).data,
    onSuccess: () => {
      toast.success(
        alese.length > 1
          ? `${alese.length} șabloane create, câte unul la ${listaDepartamente(alese)}.`
          : 'Șablon creat.',
      )
      void qc.invalidateQueries({ queryKey: CHEIE })
      laInchidere()
    },
    onError: (e) => toast.error(mesajEroare(e, 'Șablonul nu s-a putut crea.')),
  })

  const gata = !!titlu.trim() && !!prima && !!dep
  return (
    <Modal
      open
      onClose={laInchidere}
      title="Șablon nou"
      inchideLaClicInAfara={false}
      footer={
        <>
          <Button variant="secondary" onClick={laInchidere}>
            Renunță
          </Button>
          <Button disabled={!gata} loading={salveaza.isPending} onClick={() => salveaza.mutate()}>
            {alese.length > 1 ? `Creează ${alese.length} șabloane` : 'Creează'}
          </Button>
        </>
      }
    >
      <div className="grid gap-3.5">
        <label className="grid gap-1.5">
          <span className={eticheta}>Titlul task-ului</span>
          <input
            autoFocus
            className={camp}
            maxLength={200}
            value={titlu}
            onChange={(e) => setTitlu(e.target.value)}
            placeholder="Depune D300 pentru trimestrul încheiat"
          />
        </label>
        {admin ? (
          <div className="grid gap-1.5">
            <span className={eticheta}>Departamente</span>
            <AlegeDepartamente alese={alese} setAlese={setAlese} />
            {alese.length > 1 && (
              <span className="text-mic text-b4l-text-muted">
                Se face câte un șablon la {listaDepartamente(alese)}. Fiecare își are task-urile lui
                și se modifică separat.
              </span>
            )}
          </div>
        ) : (
          dep && (
            <p className="text-mic text-b4l-text-muted">
              Intră la „De preluat” în {numeDepartament(dep)}.
            </p>
          )
        )}
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="grid content-start gap-1.5">
            <span className={eticheta}>Frecvență</span>
            <select
              className={camp}
              value={cadenta}
              onChange={(e) => setCadenta(e.target.value as Cadenta)}
            >
              {CADENTE.map(([k, e]) => (
                <option key={k} value={k}>
                  {e}
                </option>
              ))}
            </select>
          </label>
          {CU_ZIUA_LUNII.includes(cadenta) && (
            <label className="grid content-start gap-1.5">
              <span className={eticheta}>Ziua lunii</span>
              <input
                type="number"
                min={1}
                max={28}
                className={camp}
                value={ziuaLunii}
                onChange={(e) =>
                  setZiuaLunii(Math.min(28, Math.max(1, Number(e.target.value) || 1)))
                }
              />
            </label>
          )}
          <label className="grid content-start gap-1.5">
            <span className={eticheta}>Prima apariție</span>
            <input
              type="date"
              className={camp}
              min={aziIso()}
              value={prima}
              onChange={(e) => setPrima(e.target.value)}
            />
          </label>
        </div>
        <label className="grid gap-1.5">
          <span className={eticheta}>
            Detalii <span className="font-normal text-b4l-text-dim">(opțional)</span>
          </span>
          <textarea
            rows={3}
            className={camp}
            maxLength={2000}
            value={descriere}
            onChange={(e) => setDescriere(e.target.value)}
          />
        </label>
        <p className="text-mic text-b4l-text-dim">
          Ziua lunii merge până la 28: cu 31 s-ar sări februarie.
        </p>
      </div>
    </Modal>
  )
}
